var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __restKey = (key) => typeof key === "symbol" ? key : key + "";
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
import { freeze, enableMapSet, enablePatches, produce, applyPatches } from "immer";
const isAncestor = (ancestor, path) => {
  return ancestor.length <= path.length && ancestor.every((v, i) => v === "*" || path[i] === "*" || String(v) === String(path[i]));
};
const get = (value, path) => {
  for (const part of path) {
    if (value instanceof Object && part in value)
      value = value[part];
    else {
      value = void 0;
      break;
    }
  }
  return value;
};
const split = (value, path) => {
  const [first, ...rest] = path;
  if (first === void 0)
    return [value, []];
  if (rest.length === 0) {
    if (first === "*")
      return [{}, Object.entries(value).map(([k, v]) => ({ path: [k], value: v }))];
    if (!(first in value))
      return [value, []];
    const _a = value, { [first]: subValue } = _a, newValue2 = __objRest(_a, [__restKey(first)]);
    return [newValue2, [{ path: [first], value: subValue }]];
  }
  const newValue = __spreadValues({}, value);
  const subValues = new Array();
  for (const key of first === "*" ? Object.keys(value) : [first]) {
    if (!(newValue[key] instanceof Object))
      return [value, []];
    const result = split(newValue[key], rest);
    newValue[key] = result[0];
    subValues.push(...result[1].map((s) => ({ path: [key, ...s.path], value: s.value })));
  }
  return [newValue, subValues];
};
class StorePersist {
  constructor(store, storage, options = {}) {
    this.store = store;
    this.storage = storage;
    this.options = options;
    this.replayPatches = new Array();
    this.isInitialized = false;
    this.whenInitialized = this.load();
    this.isStopped = false;
    this.saveQueue = new Array();
    this.isSaving = false;
    if (!options.paths) {
      this.paths = [{ path: [], throttleMs: options.throttleMs }];
    } else {
      this.paths = options.paths.map((p) => {
        var _a;
        return {
          path: (typeof p === "object" ? p.path : p).split("."),
          throttleMs: (_a = typeof p === "object" ? p.throttleMs : void 0) != null ? _a : options.throttleMs
        };
      });
    }
    this.paths.sort((a, b) => b.path.length - a.path.length);
    this.watch();
  }
  async load() {
    var _a;
    try {
      const storage = this.storage;
      let keys;
      if ("keys" in storage) {
        keys = await storage.keys();
      } else {
        const length = storage.length instanceof Function ? await storage.length() : storage.length;
        keys = new Array();
        for (let i = 0; i < length; i++) {
          const key = await storage.key(i);
          if (key !== null)
            keys.push(key);
        }
      }
      keys.sort((a, b) => a.length - b.length);
      const patches = new Array();
      for (const key of keys) {
        const value = await storage.getItem(key);
        if (value) {
          patches.push({
            path: key.split("."),
            op: "replace",
            value: value === "undefined" ? void 0 : JSON.parse(value),
            persist: this
          });
        }
      }
      if (this.isStopped) {
        return false;
      }
      if (patches.length > 0) {
        this.store.applyPatches(patches);
      }
      this.isInitialized = true;
      if (this.replayPatches.length > 0) {
        this.store.applyPatches(this.replayPatches);
        this.replayPatches = [];
      }
      return true;
    } catch (e) {
      ((_a = this.options.log) != null ? _a : console.error)("Failed to load storePersist:", e);
      return false;
    }
  }
  watch() {
    this.sub = this.store.subscribePatches((patches) => {
      for (const patch of patches) {
        if (patch.persist === this) {
          continue;
        } else if (this.isInitialized === true) {
          this.addToSaveQueue(patch.path);
        } else {
          this.replayPatches.push(patch);
        }
      }
    });
  }
  addToSaveQueue(path) {
    var _a;
    const match = this.paths.find((p) => isAncestor(p.path, path));
    if (!match)
      return;
    path = path.slice(0, match.path.length);
    const t = Date.now() + ((_a = match.throttleMs) != null ? _a : 0);
    if (this.saveQueue.some((i) => i.t <= t && isAncestor(i.path, path)))
      return;
    this.saveQueue.push({ path, t });
    this.saveQueue.sort((a, b) => a.t - b.t);
    this.run();
  }
  async run() {
    if (this.saveTimeout)
      clearTimeout(this.saveTimeout);
    const next = this.saveQueue[0];
    if (!next)
      return;
    if (next.t > Date.now()) {
      this.saveTimeout = setTimeout(() => this.run(), next.t - Date.now());
      return;
    }
    try {
      this.isSaving = true;
      this.saveQueue.shift();
      await this.save(next.path);
    } finally {
      this.isSaving = false;
      this.run();
    }
  }
  async save(path) {
    var _a, _b;
    const { store, storage } = this;
    let value = get(store.getState(), path);
    const subPaths = this.paths.filter((p) => isAncestor(path, p.path) && p.path.length > path.length).map((p) => p.path.slice(path.length));
    const subValues = new Array();
    for (const subPath of subPaths) {
      const result2 = split(value, subPath);
      value = result2[0];
      subValues.push(...result2[1].map((s) => ({ path: [...path, ...s.path], value: s.value })));
    }
    const result = storage.setItem(path.join("."), (_a = JSON.stringify(value)) != null ? _a : "undefined");
    if (result instanceof Promise)
      await result;
    for (const { path: path2, value: value2 } of subValues) {
      const result2 = storage.setItem(path2.join("."), (_b = JSON.stringify(value2)) != null ? _b : "undefined");
      if (result2 instanceof Promise)
        await result2;
    }
  }
  stop() {
    var _a;
    this.isStopped = true;
    (_a = this.sub) == null ? void 0 : _a.call(this);
  }
}
function hash(arg) {
  return JSON.stringify({ arg }, (_key, value) => {
    if (value instanceof Object && !(value instanceof Array)) {
      const obj = {};
      for (const key of Object.keys(value).sort()) {
        obj[key] = value[key];
      }
      return obj;
    }
    return value;
  });
}
var react = function equal(a, b) {
  if (a === b)
    return true;
  if (a && b && typeof a == "object" && typeof b == "object") {
    if (a.constructor !== b.constructor)
      return false;
    var length, i, keys;
    if (Array.isArray(a)) {
      length = a.length;
      if (length != b.length)
        return false;
      for (i = length; i-- !== 0; )
        if (!equal(a[i], b[i]))
          return false;
      return true;
    }
    if (a instanceof Map && b instanceof Map) {
      if (a.size !== b.size)
        return false;
      for (i of a.entries())
        if (!b.has(i[0]))
          return false;
      for (i of a.entries())
        if (!equal(i[1], b.get(i[0])))
          return false;
      return true;
    }
    if (a instanceof Set && b instanceof Set) {
      if (a.size !== b.size)
        return false;
      for (i of a.entries())
        if (!b.has(i[0]))
          return false;
      return true;
    }
    if (ArrayBuffer.isView(a) && ArrayBuffer.isView(b)) {
      length = a.length;
      if (length != b.length)
        return false;
      for (i = length; i-- !== 0; )
        if (a[i] !== b[i])
          return false;
      return true;
    }
    if (a.constructor === RegExp)
      return a.source === b.source && a.flags === b.flags;
    if (a.valueOf !== Object.prototype.valueOf)
      return a.valueOf() === b.valueOf();
    if (a.toString !== Object.prototype.toString)
      return a.toString() === b.toString();
    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length)
      return false;
    for (i = length; i-- !== 0; )
      if (!Object.prototype.hasOwnProperty.call(b, keys[i]))
        return false;
    for (i = length; i-- !== 0; ) {
      var key = keys[i];
      if (key === "_owner" && a.$$typeof) {
        continue;
      }
      if (!equal(a[key], b[key]))
        return false;
    }
    return true;
  }
  return a !== a && b !== b;
};
function createSelector(stringSelector) {
  const parts = stringSelector.split(".");
  return (state) => parts.reduce((v, p) => v instanceof Object && p in v ? v[p] : void 0, state);
}
function setWithSelector(state, stringSelector, value) {
  const parts = stringSelector.split(".");
  const prefix = parts.slice(0, -1);
  const last = parts[parts.length - 1];
  for (const part of prefix) {
    if (state instanceof Object && part in state) {
      state = state[part];
    } else {
      throw Error(`Could not set ${stringSelector} because path ${stringSelector} doesn't exist`);
    }
  }
  if (state instanceof Object) {
    state[last] = value;
  } else {
    throw Error(`Could not set ${stringSelector} because path ${stringSelector} doesn't exist`);
  }
}
function throttle(fn, ms) {
  let last = 0;
  let lastArgs;
  let timeout;
  function run() {
    const args = lastArgs;
    last = Date.now();
    lastArgs = void 0;
    timeout = void 0;
    fn(...args);
  }
  return function(...args) {
    const now = Date.now();
    lastArgs = args;
    if (timeout)
      ;
    else if (now < last + ms) {
      timeout = setTimeout(run, last + ms - now);
    } else {
      run();
    }
  };
}
const RESTART_UPDATE = Symbol("RESTART_UPDATE");
class Store {
  constructor(state, { log = (...data) => console.error(...data) } = {}) {
    this.state = state;
    this.subscriptions = new Set();
    this.batchCounter = 0;
    this.reactions = new Set();
    this.patches = new Array();
    freeze(state, true);
    enableMapSet();
    enablePatches();
    this.options = {
      log
    };
  }
  getState() {
    return this.state;
  }
  update(update) {
    this.checkLock();
    this.state = produce(this.state, (draft) => update(draft, this.state), (patches) => this.patches.push(...patches));
    this.notify();
  }
  set(state) {
    this.checkLock();
    this.state = produce(this.state, () => state, (patches) => this.patches.push(...patches));
    this.notify();
  }
  applyPatches(patches) {
    this.checkLock();
    this.state = applyPatches(this.state, patches);
    this.patches.push(...patches);
    this.notify();
  }
  batchUpdates(fn) {
    try {
      this.batchCounter++;
      fn();
    } finally {
      this.batchCounter--;
      if (this.batchCounter === 0) {
        this.notify();
      }
    }
  }
  subscribe(...args) {
    let selector, listener, options;
    if (args[1] instanceof Function) {
      [selector, listener, options] = args;
      if (typeof selector === "string") {
        selector = createSelector(selector);
      }
    } else {
      [selector, listener, options] = [(x) => x, ...args];
    }
    const { runNow = true, throttle: throttle$1 = 0, compare = react } = options != null ? options : {};
    const throttledListener = throttle$1 ? throttle(listener, throttle$1) : listener;
    let value = selector(this.state);
    const internalListener = (state, _patches, init) => {
      try {
        const oldValue = value;
        value = selector(state);
        if (!init && compare(value, oldValue))
          return;
        (init ? listener : throttledListener)(value, oldValue, this.state);
      } catch (e) {
        this.options.log("Failed to execute listener:", e);
      }
    };
    this.subscriptions.add(internalListener);
    if (runNow)
      internalListener(this.state, [], true);
    return () => {
      this.subscriptions.delete(internalListener);
    };
  }
  addReaction(...args) {
    let selector, reaction, options;
    if (args[1] instanceof Function) {
      [selector, reaction, options] = args;
      if (typeof selector === "string") {
        selector = createSelector(selector);
      }
    } else {
      [selector, reaction, options] = [(x) => x, ...args];
    }
    const { runNow = true, compare = react } = options != null ? options : {};
    let value = selector(this.state);
    const internalListener = (init) => {
      let hasChanged = false;
      try {
        this.lock = "reaction";
        const oldValue = value;
        value = selector(this.state);
        if (!init && compare(value, oldValue))
          return;
        this.state = produce(this.state, (draft) => reaction(value, draft, this.state, oldValue), (patches) => {
          hasChanged = true;
          this.patches.push(...patches);
        });
      } catch (e) {
        this.options.log("Failed to execute reaction:", e);
      } finally {
        delete this.lock;
      }
      if (hasChanged && !init)
        throw RESTART_UPDATE;
      if (hasChanged && init)
        this.notify();
    };
    this.reactions.add(internalListener);
    if (runNow)
      internalListener(true);
    return () => {
      this.reactions.delete(internalListener);
    };
  }
  subscribePatches(listener) {
    const internalListener = (_state, patches) => {
      try {
        listener(patches);
      } catch (e) {
        this.options.log("Failed to execute patch listener:", e);
      }
    };
    this.subscriptions.add(internalListener);
    return () => {
      this.subscriptions.delete(internalListener);
    };
  }
  checkLock() {
    if (this.lock === "reaction") {
      throw Error("You cannot call update from within a reaction. Use the passed draft instead.");
    }
  }
  notify() {
    if (this.batchCounter > 0 || this.patches.length === 0)
      return;
    try {
      for (const reaction of this.reactions) {
        reaction();
      }
    } catch (e) {
      return this.notify();
    }
    const patches = this.patches;
    this.patches = [];
    this.batchUpdates(() => {
      for (const subscription of this.subscriptions) {
        subscription(this.state, patches);
      }
    });
  }
}
export { Store as S, StorePersist as a, createSelector as c, hash as h, react as r, setWithSelector as s };
//# sourceMappingURL=store.js.map
